/* Generated by uc-options-engine/tools/build-theme-cart-js.js — do not edit. */
(function () {
  "use strict";
// @ts-check

/**
 * Cart integrity for configured products.
 *
 * Follows the Auto-Gift Engine's shape deliberately (assets/custom.js): an
 * idempotent reconcile that computes what the cart SHOULD contain, diffs it
 * against what it HAS, and applies the difference. `desired === actual` being a
 * no-op is what stops cart:change -> mutate -> cart:change looping, and
 * MAX_RECONCILE_PASSES is the backstop against a rule bug spinning the browser.
 *
 * Three things can go wrong between add-to-cart and checkout, and all three
 * cost real money:
 *
 *   1. The customer removes a configured product. Its charge and add-on lines
 *      survive, and they pay for options on a product they no longer own.
 *   2. The customer raises the quantity to 2. One charge covers two products,
 *      so half the customisation is free.
 *   3. A charge line survives with no parent at all — same as (1) but arrived
 *      at by a different route, e.g. an app or another tab.
 *
 * Reconcile fixes all three by treating the PRODUCT lines as the truth and
 * making every other line in the group agree with them.
 *
 * Pure: the cart API is injected, so the whole thing runs against a simulator
 * in tests rather than needing a storefront.
 */

const GROUP = "_uc_group";
const ROLE = "_uc_role";
const FOR = "_uc_for";
const FROM = "_uc_from";
/** UncoverInsights reads this name to attribute a charge to its parent. */
const INSIGHTS_ID = "_cl_options_id";

const MAX_RECONCILE_PASSES = 5;

/**
 * @param {{cart: any, chargeVariantFor?: (amount:number)=>string|null, log?: Function}} deps
 */
function createCartSync({ cart, log = () => {} }) {

  /** Group the cart's lines by our token. Lines without one are not ours. */
  function groupLines(lines) {
    const groups = new Map();
    for (const line of lines) {
      const token = line.properties?.[GROUP];
      if (!token) continue;
      const role = line.properties?.[ROLE];
      if (!role) continue;

      let group = groups.get(token);
      if (!group) {
        group = { token, products: [], charges: [], addons: [] };
        groups.set(token, group);
      }
      if (role === "product") group.products.push(line);
      else if (role === "charge") group.charges.push(line);
      else if (role === "addon") group.addons.push(line);
    }
    return groups;
  }

  /**
   * What each non-product line's quantity should be, given its parent.
   * Returns a list of {key, quantity} changes; quantity 0 means remove.
   */
  function plan(groups) {
    const changes = [];

    for (const group of groups.values()) {
      const parent = group.products[0];

      // No parent: every remaining line in the group is an orphan. This is the
      // leak that matters most — the customer is being charged for options on
      // a product that is no longer in their cart.
      if (!parent) {
        for (const line of [...group.charges, ...group.addons]) {
          changes.push({ key: line.key, quantity: 0, why: "orphan" });
        }
        continue;
      }

      // Two product lines sharing a token means the token was reused. Touching
      // anything here risks charging the wrong line, so leave the group alone
      // and say so — an untidy cart beats a mispriced one.
      if (group.products.length > 1) {
        log("duplicate token, leaving group alone", group.token);
        continue;
      }

      const parentQty = parent.quantity;
      if (!Number.isInteger(parentQty) || parentQty < 1) continue;

      // One charge line per priced option, each covering one unit of the
      // parent. Two of the product means twice the customisation.
      for (const line of group.charges) {
        if (line.quantity !== parentQty) {
          changes.push({ key: line.key, quantity: parentQty, why: "charge qty" });
        }
      }

      // Add-ons carry their own per-unit multiple in _uc_per, set when added.
      for (const line of group.addons) {
        const per = Number(line.properties?.["_uc_per"]) || 1;
        const want = per * parentQty;
        if (line.quantity !== want) {
          changes.push({ key: line.key, quantity: want, why: "addon qty" });
        }
      }
    }

    return changes;
  }

  /**
   * @returns {Promise<{mutated:boolean, changes:any[]}>}
   */
  async function reconcile(pass = 0) {
    if (pass >= MAX_RECONCILE_PASSES) {
      log("pass cap reached, giving up");
      return { mutated: false, changes: [] };
    }

    const current = await cart.get();
    const changes = plan(groupLines(current.items || []));
    if (changes.length === 0) return { mutated: false, changes: [] };

    for (const change of changes) {
      // Keys are re-read from a fresh cart on every pass, because changing one
      // line can renumber the others.
      const ok = await cart.change({ id: change.key, quantity: change.quantity });
      if (!ok) log("could not apply", change);
    }

    const next = await reconcile(pass + 1);
    return { mutated: true, changes: changes.concat(next.changes) };
  }

  /**
   * Add a configured product and everything that belongs with it, in ONE
   * request. A partial add — product on, charge missing — is an underpriced
   * order, so it must not be possible for the first call to succeed and the
   * second to fail.
   */
  async function addConfigured({ lines }) {
    if (!lines?.length) throw new Error("nothing to add");
    const ok = await cart.add(lines.map(toCartItem));
    return ok;
  }

  return { reconcile, addConfigured, plan, groupLines };
}

function toCartItem(line) {
  return { id: line.id, quantity: line.quantity, properties: line.properties };
}

// @ts-check

/**
 * Wires the engine into the Impact theme. The only file that knows anything
 * about Shopify or this particular theme; everything below it is portable.
 *
 * Three integration points, each with a reason it works the way it does:
 *
 *   1. ADD TO CART is intercepted rather than augmented. A configured product
 *      is several lines — the product, one charge per paid option, any add-ons
 *      — and a product form can only express one. Two sequential requests could
 *      half-succeed and leave a product with no charge line, which is an
 *      underpriced order, so all lines go in ONE POST.
 *
 *   2. REPAINT is `cart:refresh`, not `cart:change`. The drawer paints HTML
 *      captured from the /cart/add.js response, rendered BEFORE our lines
 *      existed — so cart:change alone shows a drawer missing the charge.
 *      Documented the hard way by the Auto-Gift Engine; see assets/custom.js.
 *
 *   3. VARIANT CHANGE re-resolves. 23 conditions in the live ruleset key on
 *      variant_title — "no cum tube on X-Small" and similar — so switching size
 *      can legitimately add or remove options.
 */

const ROOT = "[data-uc-options-root]";

function boot({
  doc = document,
  win = window,
  routes = win.Shopify?.routes?.root || "/",
  log = (...args) => console.warn("[options]", ...args),
} = {}) {
  const root = doc.querySelector(ROOT);
  const rules = win.UC_OPTIONS_RULES;
  const product = win.UC_OPTIONS_PRODUCT;

  /*
   * THE CART WORK RUNS EVERYWHERE; THE OPTIONS WORK ONLY WHERE THERE ARE OPTIONS.
   *
   * Both used to live below this early return, which meant a page with no
   * options got neither — and two things quietly depended on that being wrong.
   * The header count showed the raw item total on the cart page, on collections
   * and on the home page, so one configured dildo read as "2" everywhere except
   * the product page itself. And cart integrity only ran on product pages, so
   * removing the product from the CART page left its Customisation charge
   * behind, payable, with nothing attached to it.
   */
  bootCart({ doc, win, routes, log });

  // Nothing more to do on a product with no options configured. Not an error.
  if (!root || !rules || !product) return null;

  const chargePrices = win.UC_OPTIONS_CHARGE_PRICES || {};
  const addonPrices = win.UC_OPTIONS_ADDON_PRICES || {};
  /* Only the sold-out ids are listed; absence means available, so a snippet
   * that failed to render cannot take a whole range off sale. */
  const soldOut = win.UC_OPTIONS_ADDON_SOLD_OUT || [];
  const variantPrices = product.variantPrices || {};
  if (Object.keys(chargePrices).length === 0) {
    // Options will render, priced silently. Better than a wrong price, but it
    // means the charge collection is missing — see uc-options-charge-prices.liquid.
    log("no charge prices available; options will show without prices");
  }

  const money = makeMoneyFormatter(win);
  const chargeVariantFor = win.UCOptions.makeChargeLookup({ variants: rules.charge || {} });

  const ui = win.UCOptions.mount({
    root,
    rules,
    product,
    variant: product.variant || {},
    chargePrices,
    addonPrices,
    soldOut,
    basePrice: product.variant?.price ?? 0,
    formatMoney: money,
    chargeVariantFor,
    onChange: (state) => {
      // Let the theme's own price block show the configured total.
      doc.dispatchEvent(new CustomEvent("uc-options:change", { detail: state, bubbles: true }));
    },
  });

  // mount() fires onChange synchronously, before anything registered in this
  // same tick can be listening. Re-announce once the tick drains so the
  // theme's price block gets initial state without having to poll ui.state.
  setTimeout(() => {
    doc.dispatchEvent(new CustomEvent("uc-options:change", { detail: ui.state, bubbles: true }));
  }, 0);

  /* ── Add to cart ─────────────────────────────────────────────────── */

  /*
   * Bound on the DOCUMENT in the capture phase, not on the form.
   *
   * Impact's form is a customized built-in (`<form is="product-form">`), so its
   * own submit listener is attached while the element is parsed — before this
   * deferred script runs. Listeners on the TARGET element fire in registration
   * order whatever their capture flag, so binding to the form would run second,
   * after the theme had already added the product with none of our properties.
   *
   * Capture on an ancestor genuinely precedes the target, which is the only
   * placement that lets us take over the submit.
   */
  doc.addEventListener("submit", async (event) => {
    const form = event.target;
    if (!(form instanceof win.HTMLFormElement)) return;
    if (!/\/cart\/add/.test(form.getAttribute("action") || "")) return;

    {
      event.preventDefault();
      event.stopPropagation();

      const button = form.querySelector('[type="submit"]');
      const variantId = form.querySelector('[name="id"]')?.value || product.variant?.id;
      const quantity = Number(form.querySelector('[name="quantity"]')?.value) || 1;

      let lines;
      try {
        lines = ui.cartLines({ variantId, quantity }).lines;
      } catch (error) {
        // A required option is unanswered. Say so and stay put rather than
        // adding a half-configured product.
        log(error.message);
        doc.dispatchEvent(new CustomEvent("uc-options:invalid", {
          detail: { message: error.message, missing: ui.state.missingRequired }, bubbles: true,
        }));
        return;
      }

      button?.setAttribute("aria-busy", "true");
      try {
        /*
         * WHY THIS MIRRORS IMPACT'S OWN FORM STEP FOR STEP.
         *
         * Taking over the submit meant the theme's ProductForm never ran, and
         * with it went the thing that opens the cart drawer: it is not the add
         * request that opens it but a `variant:add` event carrying a
         * server-rendered `variant-added` section. We dispatched `cart:refresh`
         * instead, which re-renders a drawer nobody opened — so the product
         * went into the cart silently and the customer had no idea whether it
         * had worked.
         *
         * `cart:prepare-bundled-sections` asks the rest of the theme which
         * sections it needs rendered with the add, rather than assuming it is
         * only ever `variant-added`; anything else listening gets its say too.
         */
        const sectionsToBundle = ["variant-added"];
        doc.documentElement?.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", {
          bubbles: true, detail: { sections: sectionsToBundle },
        }));

        const response = await fetch(`${routes}cart/add.js`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            // Without this Shopify skips the inventory check and will happily
            // add an out-of-stock linked product.
            "X-Requested-With": "XMLHttpRequest",
          },
          body: JSON.stringify({
            items: lines,
            /* One request adds the product, the charge and every linked
             * product, so the section rendered from it already shows all of
             * them — which is what note 2 above was working around. */
            sections: sectionsToBundle.join(","),
            sections_url: `${routes}variants/${variantId}`,
          }),
        });

        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          log("add to cart failed:", body?.description || response.status);
          doc.dispatchEvent(new CustomEvent("uc-options:invalid", {
            detail: { message: body?.description || "Could not add to cart" }, bubbles: true,
          }));
          return;
        }

        /* A page cart has no drawer to open. */
        if (win.themeVariables?.settings?.cartType === "page" ||
            win.themeVariables?.settings?.pageType === "cart") {
          win.location.href = `${routes}cart`;
          return;
        }

        const added = await response.json().catch(() => ({}));
        const cart = await fetch(`${routes}cart.js`).then((r) => r.json()).catch(() => null);
        if (!cart) {
          doc.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
          return;
        }
        cart.sections = added.sections;

        /* From the FORM, bubbling — quick-buy drawers listen on the form they
         * belong to, while the cart drawer listens on the document. */
        form.dispatchEvent(new CustomEvent("variant:add", {
          bubbles: true,
          detail: { items: added.items || [added], cart },
        }));
        doc.documentElement?.dispatchEvent(new CustomEvent("cart:change", {
          bubbles: true, detail: { baseEvent: "variant:add", cart },
        }));
      } finally {
        button?.removeAttribute("aria-busy");
      }
    }
  }, true);   // capture on document — see the note above

  /* The sticky bar's own button. It submits the theme's form rather than
   * posting on its own, so validation, selling plans and everything else the
   * form carries keep working — and there is exactly one add-to-cart path to
   * reason about. */
  doc.addEventListener("click", (event) => {
    const trigger = event.target.closest?.("[data-uc-add]");
    if (!trigger || trigger.disabled) return;
    const form = doc.querySelector('form[action*="/cart/add"]');
    if (!form) return;
    event.preventDefault();
    if (typeof form.requestSubmit === "function") form.requestSubmit();
    else form.dispatchEvent(new CustomEvent("submit", { bubbles: true, cancelable: true }));
  });

  /* ── Variant changes ─────────────────────────────────────────────── */

  doc.addEventListener("variant:change", (event) => {
    const variant = event.detail?.variant;
    if (!variant) return;
    /* The price moves with the size, so the running total has to move with it.
     * Shopify's own event carries a price; the map is the fallback for themes
     * that dispatch only an id. */
    const basePrice = typeof variant.price === "number"
      ? variant.price
      : variantPrices[String(variant.id)];
    ui.refresh({ variant: { id: variant.id, title: variant.title }, basePrice });
  });

  return ui;
}

/**
 * Everything about the cart that is true on every page of the shop.
 *
 * Kept apart from the options themselves because it has nothing to do with
 * them: a customer on the cart page has no options on screen and still has a
 * header count to keep honest and orphaned charge lines to be rid of.
 */
function bootCart({ doc, win, routes, log }) {
  /*
   * Idempotent, because there are two ways in. On a product page the cart
   * bundle boots this and then the options bundle loads and boots too; on every
   * other page only the cart bundle runs. Two sets of listeners would reconcile
   * the cart twice on every change and race each other doing it.
   */
  if (win.__ucCartBooted) return;
  win.__ucCartBooted = true;

  /* ── The cart bubble counts what is being shipped ─────────────────
   *
   * A configured product is two or three cart lines, so `cart.item_count` — and
   * therefore the header bubble — said "2" for one dildo, with no way for the
   * customer to tell what the second thing was. Liquid renders the corrected
   * number (snippets/uc-cart-count.liquid), but the theme's <cart-count>
   * recomputes from the raw cart on every change and puts the wrong one back.
   *
   * Hence an observer rather than another event listener: it catches every path
   * that writes to the element, including the ones we have not gone looking
   * for, and it cannot race the theme's own async update. Writing only when the
   * value differs is what stops it chasing its own tail — the theme's setter
   * ignores an unchanged value too.
   */
  let expectedCount = null;

  const shippedCount = (cart) => (cart?.items || []).reduce(
    (total, item) => item.properties?._uc_role === "charge" ? total : total + (item.quantity || 0), 0);

  function correctCount(element) {
    if (expectedCount === null) return;
    try {
      if (element.itemCount !== expectedCount) element.itemCount = expectedCount;
    } catch {
      element.textContent = String(expectedCount);
    }
  }

  async function syncCartCount() {
    const cart = await fetch(`${routes}cart.js`).then((r) => r.json()).catch(() => null);
    if (!cart) return;
    expectedCount = shippedCount(cart);
    for (const element of doc.querySelectorAll?.("cart-count") || []) {
      if (!element.ucCountWatched && typeof win.MutationObserver === "function") {
        element.ucCountWatched = true;
        new win.MutationObserver(() => correctCount(element))
          .observe(element, { childList: true, characterData: true, subtree: true });
      }
      correctCount(element);
    }
  }

  for (const event of ["cart:change", "cart:refresh", "variant:add"]) {
    doc.addEventListener(event, () => { syncCartCount(); });
  }
  syncCartCount();

  /* ── Cart integrity ──────────────────────────────────────────────── */

  const sync = win.UCOptions.createCartSync({
    cart: {
      get: async () => (await fetch(`${routes}cart.js`)).json(),
      add: async (items) => {
        const r = await fetch(`${routes}cart/add.js`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
          body: JSON.stringify({ items }),
        });
        return r.ok;
      },
      change: async ({ id, quantity }) => {
        const r = await fetch(`${routes}cart/change.js`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id, quantity }),
        });
        return r.ok;
      },
    },
    log,
  });

  let chain = Promise.resolve();
  const reconcile = () => {
    chain = chain.then(async () => {
      const { mutated } = await sync.reconcile();
      if (!mutated) return;
      // Same repaint split the gift engine documents: cart:refresh re-renders
      // the drawer but not the cart PAGE, which reloads instead.
      if (win.themeVariables?.settings?.pageType === "cart") win.location.reload();
      else doc.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
    }).catch((error) => log(error));
    return chain;
  };

  doc.addEventListener("cart:change", reconcile);
  win.addEventListener("pageshow", (e) => { if (e.persisted) reconcile(); });

}

/**
 * Formats minor units in the CUSTOMER'S currency. Intl gets the symbol,
 * grouping and decimal places right per locale, which hand-rolled
 * money_format string substitution does not.
 *
 * A WHOLE AMOUNT LOSES ITS DECIMALS, because the rest of the store does the
 * same — Impact renders line prices with `money_without_trailing_zeros`, so
 * "£39.00" beside the theme's "£39" read as a different kind of number. A price
 * with real pence keeps them: £17.95 is £17.95 everywhere.
 */
function makeMoneyFormatter(win) {
  const currency = win.UC_OPTIONS_CURRENCY || win.Shopify?.currency?.active || "GBP";
  const build = (digits) => {
    try {
      return new Intl.NumberFormat(win.Shopify?.locale || undefined, {
        style: "currency", currency,
        minimumFractionDigits: digits, maximumFractionDigits: digits,
      });
    } catch {
      return null;
    }
  };

  const withPence = build(2);
  const whole = build(0);
  if (!withPence) return (minor) => `${(minor / 100).toFixed(2)}`;

  return (minor) => (minor % 100 === 0 && whole
    ? whole.format(minor / 100)
    : withPence.format(minor / 100));
}


  window.UCOptions = window.UCOptions || {};
  window.UCOptions.createCartSync = window.UCOptions.createCartSync || createCartSync;
  window.UCOptions.bootCart = window.UCOptions.bootCart || bootCart;

  /* The cart is the same cart whether or not this page sells options. */
  function start() {
    bootCart({
      doc: document,
      win: window,
      routes: window.Shopify?.routes?.root || "/",
      log: (...args) => console.warn("[options:cart]", ...args),
    });
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start, { once: true });
  } else {
    start();
  }
})();
